import type { ParameterDiscoveryObjectiveMode, ParameterDistribution, ParameterValueStats } from '@einhaus/project314-types';
import type { ParameterPreferenceIdentity } from '@/hooks/useParameterPreferences';
import dayjs from 'dayjs';
import type {
    AvailableAnalysis,
    BucketAlgorithm,
    BucketParameterIdentity,
    BuildBucketsResult,
    CanonicalParameterFeatureIdentity,
    GroupedJobs,
    JobCombo,
    ParameterAnalysisTableSource,
    ParameterBucket,
    ParameterBucketBoundary,
    ParameterDiscoveryJob,
    ParameterGroup,
    ParameterDistributionQueryInput,
    ParameterDiscoveryMetric,
    ParameterTableRow,
    ScorecardAddRuleContextFeatureRef,
    ScorecardAuthoringCandidate
} from './types';

type BucketAccumBase = {
    count: number;
    sumProfit: number;
    sumProfitSq: number;
    winsCount: number;
};

type BucketAccum = BucketAccumBase & {
    min: number;
    max: number;
};

type BoundaryBucketAccum = BucketAccumBase & {
    boundary: ParameterBucketBoundary;
};

export const hasNonEmptyString = (value: string | null | undefined): value is string => typeof value === 'string' && value.length > 0;

export const buildParameterDistributionQueryInput = (params: {
    experimentId: number;
    duration: string;
    horizonSeconds: number;
    identity: BucketParameterIdentity | null;
    metric: ParameterDiscoveryMetric;
    objectiveMode: ParameterDiscoveryObjectiveMode;
    realizedOutcomeSetId: number | null;
}): ParameterDistributionQueryInput => ({
    experimentId: params.experimentId,
    duration: params.duration,
    horizonSeconds: params.horizonSeconds,
    contextFeatureRef: params.identity?.contextFeatureRef ?? '',
    metric: params.metric,
    objectiveMode: params.objectiveMode,
    realizedOutcomeSetId: params.realizedOutcomeSetId,
    ...(hasNonEmptyString(params.identity?.featureId) ? { featureId: params.identity.featureId } : {}),
    ...(hasNonEmptyString(params.identity?.featureKey) ? { featureKey: params.identity.featureKey } : {})
});

export const isFiniteNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

export const clampBucketCount = (value: number): number => {
    const minBuckets = 2;
    const maxBuckets = 30;
    if (!Number.isFinite(value)) return 10;
    if (value < minBuckets) return minBuckets;
    if (value > maxBuckets) return maxBuckets;
    return Math.round(value);
};

export const extractNumericStats = (
    distribution: ParameterDistribution
): {
    numeric: { value: number; stats: ParameterValueStats }[];
    totalNumericCount: number;
    excludedCount: number;
} => {
    const numeric: { value: number; stats: ParameterValueStats }[] = [];
    let totalNumericCount = 0;
    let excludedCount = 0;

    for (const stats of distribution.values) {
        if (isFiniteNumber(stats.value)) {
            const { value } = stats;
            numeric.push({ value, stats });
            totalNumericCount += stats.count;
        } else {
            excludedCount += stats.count;
        }
    }

    // Sort numeric values ascending
    numeric.sort((a, b) => a.value - b.value);

    return { numeric, totalNumericCount, excludedCount };
};

const createEmptyBucketAccum = (): BucketAccum => ({
    min: Number.POSITIVE_INFINITY,
    max: Number.NEGATIVE_INFINITY,
    count: 0,
    sumProfit: 0,
    sumProfitSq: 0,
    winsCount: 0
});

const createEmptyBucketAccums = (count: number): BucketAccum[] => Array.from({ length: count }, createEmptyBucketAccum);

const addStatsToAccum = (accum: BucketAccumBase, stats: ParameterValueStats): void => {
    accum.count += stats.count;
    accum.sumProfit += stats.sumProfit;
    accum.sumProfitSq += stats.sumProfitSq;
    accum.winsCount += stats.winsCount;
};

const addValueStatsToAccum = (accum: BucketAccum, value: number, stats: ParameterValueStats): void => {
    accum.min = Math.min(accum.min, value);
    accum.max = Math.max(accum.max, value);
    addStatsToAccum(accum, stats);
};

const clampBucketIndex = (index: number, bucketCount: number): number => {
    if (index < 0) return 0;
    if (index >= bucketCount) return bucketCount - 1;
    return index;
};

const getBucketIndex = (value: number, startValue: number, width: number, bucketCount: number): number => {
    const index = width === 0 ? 0 : Math.floor((value - startValue) / width);
    return clampBucketIndex(index, bucketCount);
};

const createParameterBucket = (
    boundary: ParameterBucketBoundary,
    accum: BucketAccumBase,
    baseline: number,
    denominator: number
): ParameterBucket => {
    const avgProfit = accum.sumProfit / accum.count;
    const meanSq = accum.sumProfitSq / accum.count;
    const variance = Math.max(0, meanSq - avgProfit * avgProfit);
    const stdDev = Math.sqrt(variance);
    const winRate = (accum.winsCount / accum.count) * 100;
    const coverage = accum.count / denominator;
    const lift = avgProfit - baseline;
    const se = accum.count > 1 && stdDev > 0 ? stdDev / Math.sqrt(accum.count) : 0;

    return {
        boundary,
        count: accum.count,
        avgProfit,
        winRate,
        stdDev,
        coverage,
        lift,
        ciLower: lift - 1.96 * se,
        ciUpper: lift + 1.96 * se
    };
};

const createBucketsFromAccums = (accums: BucketAccum[], baseline: number, denominator: number): ParameterBucket[] => {
    return accums
        .filter((bucket) => bucket.count > 0)
        .map((bucket) => createParameterBucket({ min: bucket.min, max: bucket.max }, bucket, baseline, denominator));
};

const buildEqualWidthBuckets = (distribution: ParameterDistribution, bucketCount: number): BuildBucketsResult => {
    const { numeric, totalNumericCount, excludedCount } = extractNumericStats(distribution);

    if (numeric.length === 0 || totalNumericCount === 0) {
        return { buckets: [], totalCount: 0, excludedCount };
    }

    const count = clampBucketCount(bucketCount);
    const minValue = numeric[0].value;
    const maxValue = numeric[numeric.length - 1].value;

    if (minValue === maxValue) {
        // Degenerate case: all values identical -> single bucket
        const singleAccum = numeric.reduce(
            (acc, { stats }) => {
                addStatsToAccum(acc, stats);
                return acc;
            },
            { count: 0, sumProfit: 0, sumProfitSq: 0, winsCount: 0 }
        );

        const bucket = createParameterBucket({ min: minValue, max: maxValue }, singleAccum, distribution.baseline, totalNumericCount);

        return { buckets: [bucket], totalCount: singleAccum.count, excludedCount };
    }

    const width = (maxValue - minValue) / count;
    const accums = createEmptyBucketAccums(count);

    // Assign each value to a bucket based on linear position
    for (const { value, stats } of numeric) {
        const idx = getBucketIndex(value, minValue, width, accums.length);
        addValueStatsToAccum(accums[idx], value, stats);
    }

    const buckets = createBucketsFromAccums(accums, distribution.baseline, totalNumericCount);

    return { buckets, totalCount: totalNumericCount, excludedCount };
};

const buildLogWidthBuckets = (distribution: ParameterDistribution, bucketCount: number): BuildBucketsResult => {
    const { numeric, totalNumericCount, excludedCount } = extractNumericStats(distribution);

    const positive = numeric.filter((n) => n.value > 0);

    if (positive.length === 0) {
        // Fall back to equal width when log-space is not applicable
        return buildEqualWidthBuckets(distribution, bucketCount);
    }

    const count = clampBucketCount(bucketCount);
    const minValue = positive[0].value;
    const maxValue = positive[positive.length - 1].value;

    if (minValue === maxValue) {
        return buildEqualWidthBuckets(distribution, bucketCount);
    }

    const logMin = Math.log10(minValue);
    const logMax = Math.log10(maxValue);
    const width = (logMax - logMin) / count;
    const accums = createEmptyBucketAccums(count);

    for (const { value, stats } of positive) {
        const logValue = Math.log10(value);
        const idx = getBucketIndex(logValue, logMin, width, accums.length);
        addValueStatsToAccum(accums[idx], value, stats);
    }

    const totalPositiveCount = accums.reduce((sum, b) => sum + b.count, 0);
    const denom = totalPositiveCount > 0 ? totalPositiveCount : totalNumericCount;
    const buckets = createBucketsFromAccums(accums, distribution.baseline, denom);

    return { buckets, totalCount: denom, excludedCount };
};

const buildEqualCountBuckets = (distribution: ParameterDistribution, bucketCount: number): BuildBucketsResult => {
    const { numeric, totalNumericCount, excludedCount } = extractNumericStats(distribution);

    if (numeric.length === 0 || totalNumericCount === 0) {
        return { buckets: [], totalCount: 0, excludedCount };
    }

    const count = clampBucketCount(bucketCount);
    const idealSize = totalNumericCount / count;

    const bucketsAccum: BucketAccum[] = [];
    let current: BucketAccum | null = null;
    let bucketIndex = 0;

    for (const { value, stats } of numeric) {
        if (!current) {
            current = {
                min: value,
                max: value,
                count: 0,
                sumProfit: 0,
                sumProfitSq: 0,
                winsCount: 0
            };
        }

        addValueStatsToAccum(current, value, stats);

        const threshold = idealSize * (bucketIndex + 1);

        if (current.count >= threshold && bucketIndex < count - 1) {
            bucketsAccum.push(current);
            current = null;
            bucketIndex += 1;
        }
    }

    if (current && current.count > 0) {
        bucketsAccum.push(current);
    }

    const buckets = createBucketsFromAccums(bucketsAccum, distribution.baseline, totalNumericCount);

    return { buckets, totalCount: totalNumericCount, excludedCount };
};

export const buildBucketsForDistribution = (
    distribution: ParameterDistribution,
    algorithm: BucketAlgorithm,
    bucketCount: number
): BuildBucketsResult => {
    if (algorithm === 'equalCount') return buildEqualCountBuckets(distribution, bucketCount);
    if (algorithm === 'logWidth') return buildLogWidthBuckets(distribution, bucketCount);
    return buildEqualWidthBuckets(distribution, bucketCount);
};

const createBoundaryBucketAccums = (boundaries: ParameterBucketBoundary[]): BoundaryBucketAccum[] => {
    return boundaries.map((boundary) => ({
        boundary: { ...boundary },
        count: 0,
        sumProfit: 0,
        sumProfitSq: 0,
        winsCount: 0
    }));
};

const getBoundaryMatchIndex = (accums: BoundaryBucketAccum[], value: number): number => {
    const exactIndex = accums.findIndex((bucket) => value >= bucket.boundary.min && value <= bucket.boundary.max);

    if (exactIndex !== -1) return exactIndex;

    const firstBoundary = accums[0].boundary;
    const lastBoundary = accums[accums.length - 1].boundary;
    const eps = Math.abs(lastBoundary.max - firstBoundary.min) / 1_000_000 || 1e-6;

    return accums.findIndex((bucket) => value >= bucket.boundary.min - eps && value <= bucket.boundary.max + eps);
};

const createBucketsFromBoundaryAccums = (accums: BoundaryBucketAccum[], baseline: number, denominator: number): ParameterBucket[] => {
    return accums
        .filter((accum) => accum.count > 0)
        .map((accum) => createParameterBucket({ ...accum.boundary }, accum, baseline, denominator));
};

export const buildBucketsFromBoundaries = (
    distribution: ParameterDistribution,
    boundaries: ParameterBucketBoundary[]
): BuildBucketsResult => {
    if (!boundaries.length || distribution.values.length === 0) {
        const excluded = distribution.values.reduce((s, v) => s + v.count, 0);
        return { buckets: [], totalCount: 0, excludedCount: excluded };
    }

    const sorted = [...boundaries].sort((a, b) => a.min - b.min);
    const accums = createBoundaryBucketAccums(sorted);
    let excludedCount = 0;

    for (const stats of distribution.values) {
        if (!isFiniteNumber(stats.value)) {
            excludedCount += stats.count;
            continue;
        }

        const v = stats.value;
        const idx = getBoundaryMatchIndex(accums, v);

        if (idx === -1) {
            excludedCount += stats.count;
            continue;
        }

        addStatsToAccum(accums[idx], stats);
    }

    const totalCount = accums.reduce((s, b) => s + b.count, 0);
    if (totalCount === 0) return { buckets: [], totalCount: 0, excludedCount };

    const buckets = createBucketsFromBoundaryAccums(accums, distribution.baseline, totalCount);

    return { buckets, totalCount, excludedCount };
};

export const requireScorecardCandidateContextFeatureRef = (
    scorecardCandidate: ScorecardAuthoringCandidate | null | undefined,
    parameterPath: string
): ScorecardAddRuleContextFeatureRef => {
    if (!scorecardCandidate) {
        throw new Error(`Canonical scorecard candidate is missing for "${parameterPath}"`);
    }

    return scorecardCandidate.contextFeatureRef;
};

export const getParameterPrimaryIdentityLabel = (parameter: {
    path: string;
    canonicalFeature?: CanonicalParameterFeatureIdentity | null | undefined;
}): string => {
    return parameter.canonicalFeature?.featureKey ?? parameter.path;
};

type ParameterWindowRefFields = {
    offsetBars?: number | null;
    trailingMode?: string | null;
    withinBars?: number | null;
};

export type ParameterTrailingWindow = {
    kind: 'offset' | 'within';
    bars: number;
    label: string;
    badgeLabel: string;
    description: string;
};

const pluralizeBar = (bars: number): string => (bars === 1 ? 'bar' : 'bars');

const isPositiveInteger = (value: number | null | undefined): value is number => value != null && Number.isInteger(value) && value > 0;

const parseParameterWindowRefFields = (serializedContextFeatureRef: string | null | undefined): ParameterWindowRefFields | null => {
    if (!serializedContextFeatureRef) {
        return null;
    }

    try {
        return JSON.parse(serializedContextFeatureRef) as ParameterWindowRefFields;
    } catch {
        return null;
    }
};

export const getParameterTrailingWindow = (parameter: {
    canonicalFeature?: CanonicalParameterFeatureIdentity | null | undefined;
}): ParameterTrailingWindow | null => {
    const ref = parseParameterWindowRefFields(parameter.canonicalFeature?.serializedContextFeatureRef);

    if (!ref) {
        return null;
    }

    if (ref.trailingMode === 'withinBars' && isPositiveInteger(ref.withinBars)) {
        return {
            kind: 'within',
            bars: ref.withinBars,
            label: `within last ${ref.withinBars} ${pluralizeBar(ref.withinBars)}`,
            badgeLabel: `within ${ref.withinBars} ${pluralizeBar(ref.withinBars)}`,
            description:
                ref.withinBars === 1
                    ? 'Current completed decision-context bar.'
                    : `Current completed decision-context bar or previous ${ref.withinBars - 1} ${pluralizeBar(ref.withinBars - 1)}.`
        };
    }

    if (isPositiveInteger(ref.offsetBars)) {
        return {
            kind: 'offset',
            bars: ref.offsetBars,
            label: `${ref.offsetBars} ${pluralizeBar(ref.offsetBars)} ago`,
            badgeLabel: `${ref.offsetBars} ${pluralizeBar(ref.offsetBars)} ago`,
            description: `Completed bar exactly ${ref.offsetBars} ${pluralizeBar(ref.offsetBars)} before the decision-context bar.`
        };
    }

    return null;
};

export const getParameterBaseDisplayLabel = (parameter: {
    path: string;
    canonicalFeature?: CanonicalParameterFeatureIdentity | null | undefined;
}): string => {
    return parameter.canonicalFeature?.label ?? getParameterPrimaryIdentityLabel(parameter);
};

export const getParameterDisplayLabelWithWindow = (parameter: {
    path: string;
    canonicalFeature?: CanonicalParameterFeatureIdentity | null | undefined;
}): string => {
    const baseLabel = getParameterBaseDisplayLabel(parameter);
    const trailingWindow = getParameterTrailingWindow(parameter);

    return trailingWindow ? `${baseLabel} (${trailingWindow.label})` : baseLabel;
};

export const getParameterSecondaryIdentityLabel = (parameter: {
    path: string;
    canonicalFeature?: CanonicalParameterFeatureIdentity | null | undefined;
}): string | null => {
    const featureKey = parameter.canonicalFeature?.featureKey;

    if (!featureKey) {
        return null;
    }

    return parameter.path.startsWith('sha256:') ? featureKey : `${featureKey} · ${parameter.path}`;
};

export const buildParameterPreferenceIdentity = (parameter: {
    path: string;
    parameterKey?: string | null | undefined;
    canonicalFeature?: CanonicalParameterFeatureIdentity | null | undefined;
}): ParameterPreferenceIdentity => {
    return {
        preferenceKey:
            parameter.parameterKey ??
            parameter.canonicalFeature?.featureId ??
            parameter.canonicalFeature?.serializedContextFeatureRef ??
            parameter.path,
        featureId: parameter.canonicalFeature?.featureId,
        featureKey: parameter.canonicalFeature?.featureKey,
        contextFeatureRef: parameter.canonicalFeature?.serializedContextFeatureRef
    };
};

export const getBucketIdentityKey = (parameter: {
    path: string;
    parameterKey?: string | null | undefined;
    preferenceIdentity?: ParameterPreferenceIdentity | null | undefined;
    canonicalFeature?: CanonicalParameterFeatureIdentity | null | undefined;
}): string => {
    return (
        parameter.preferenceIdentity?.preferenceKey ??
        parameter.parameterKey ??
        parameter.canonicalFeature?.featureId ??
        parameter.canonicalFeature?.serializedContextFeatureRef ??
        parameter.path
    );
};

export const buildBucketParameterIdentity = (parameter: ParameterTableRow): BucketParameterIdentity => {
    const { canonicalFeature, preferenceIdentity } = parameter;

    return {
        bucketKey: getBucketIdentityKey(parameter),
        parameterPath: parameter.path,
        parameterKey: parameter.parameterKey,
        featureId: preferenceIdentity.featureId ?? canonicalFeature?.featureId,
        featureKey: preferenceIdentity.featureKey ?? canonicalFeature?.featureKey,
        contextFeatureRef: preferenceIdentity.contextFeatureRef ?? canonicalFeature?.serializedContextFeatureRef,
        canonicalFeature
    };
};

export const getGroupDisplayLabel = (group: ParameterGroup): string => {
    return group.valueLabel ?? (typeof group.value === 'boolean' ? (group.value ? 'True' : 'False') : String(group.value));
};

const getMetricValue = (
    value: number | { overall?: number | null; directionAgreement?: number | null } | undefined,
    key: 'overall' | 'directionAgreement'
): number | undefined => {
    if (typeof value === 'number') return value;
    return value?.[key] ?? undefined;
};

const weightedAverage = (
    groups: readonly ParameterGroup[],
    getValue: (group: ParameterGroup) => number | undefined
): number | undefined => {
    let weightedSum = 0;
    let totalCount = 0;

    for (const group of groups) {
        const value = getValue(group);
        if (value == null || !Number.isFinite(value)) continue;
        weightedSum += value * group.count;
        totalCount += group.count;
    }

    return totalCount > 0 ? weightedSum / totalCount : undefined;
};

export const buildParameterTableRow = (path: string, analysis: ParameterAnalysisTableSource): ParameterTableRow => {
    const { canonicalFeature } = analysis;
    const parameterKey = analysis.parameterKey ?? canonicalFeature?.featureId ?? analysis.parameterPath ?? path;

    const preferenceIdentity = buildParameterPreferenceIdentity({
        path,
        parameterKey,
        canonicalFeature
    });

    const stockGroups = analysis.groups ?? [];
    const { optionGroups } = analysis;

    return {
        path,
        parameterKey,
        preferenceIdentity,
        canonicalFeature,
        scorecardCandidate: analysis.scorecardCandidate,
        impact: analysis.impact,
        impactScore: analysis.impactScore,
        lcbScore: analysis.lcbScore,
        qValue: analysis.qValue ?? analysis.significance?.qValue ?? undefined,
        coverage: getMetricValue(analysis.coverage, 'overall'),
        stability: getMetricValue(analysis.stability, 'directionAgreement'),
        uniqueValues: analysis.uniqueValues,
        sampleSize: analysis.sampleSize,
        baseline: analysis.baseline,
        avgProfit: analysis.avgProfit ?? weightedAverage(stockGroups, (group) => group.avgProfit) ?? analysis.baseline,
        avgAlpha: weightedAverage(stockGroups, (group) => group.avgAlpha),
        beatSpyRate: weightedAverage(stockGroups, (group) => group.beatSpyRate),
        avgMaxProfit: weightedAverage(stockGroups, (group) => group.avgMaxProfit),
        avgMaxLoss: weightedAverage(stockGroups, (group) => group.avgMaxLoss),
        groups: stockGroups,
        bestGroup: analysis.bestGroup ?? analysis.bestGroups?.[0],
        adaptive: analysis.adaptive,
        modelImportance: analysis.modelImportance,
        modelImportanceStd: analysis.modelImportanceStd,
        optimizationOpportunity: analysis.optimizationOpportunity,
        optionGroups,
        optionBaseline: analysis.optionBaseline,
        hasOptionData: analysis.hasOptionData,
        optionDataCoverage: analysis.optionDataCoverage,
        optionImpactScore: analysis.optionImpactScore,
        optionLcbScore: analysis.optionLcbScore,
        optionQValue: analysis.optionQValue ?? analysis.optionSignificance?.qValue ?? undefined,
        optionCoverage: getMetricValue(analysis.optionCoverage, 'overall'),
        optionStability: getMetricValue(analysis.optionStability, 'directionAgreement'),
        optionBestGroup: analysis.optionBestGroup ?? analysis.optionBestGroups?.[0],
        optionModelImportance: analysis.optionModelImportance,
        optionModelImportanceStd: analysis.optionModelImportanceStd,
        optionOptimizationOpportunity: analysis.optionOptimizationOpportunity,
        optionAvgProfit:
            analysis.optionAvgProfit ??
            weightedAverage(optionGroups ?? stockGroups, (group) => group.optionAvgProfit ?? group.avgProfit) ??
            analysis.optionBaseline,
        optionWinRate: analysis.optionWinRate ?? weightedAverage(optionGroups ?? stockGroups, (group) => group.optionWinRate),
        optionBeatStockRate:
            analysis.optionBeatStockRate ?? weightedAverage(optionGroups ?? stockGroups, (group) => group.optionBeatStockRate)
    };
};

export const readPersistedScorecardId = (storageKey: string | null): number | null => {
    if (!storageKey) return null;

    try {
        const stored = localStorage.getItem(storageKey);
        const parsed = stored ? Number(stored) : NaN;
        return Number.isFinite(parsed) ? parsed : null;
    } catch {
        return null;
    }
};

// Helper: parse group label to selector
export const toSelector = (label: string, rawValue: unknown) => {
    try {
        const str = String(label);
        const m = str.match(/(-?\d+(?:\.\d+)?)\s*(?:-|to)\s*(-?\d+(?:\.\d+)?)/i);

        if (m) {
            const min = parseFloat(m[1]);
            const max = parseFloat(m[2]);
            if (Number.isFinite(min) && Number.isFinite(max)) return { type: 'range', min, max } as const;
        }
    } catch (err) {
        console.error(err);
    }

    // Fallback to value selector based on display value
    const v = typeof rawValue === 'string' || typeof rawValue === 'number' || typeof rawValue === 'boolean' ? rawValue : String(label);
    return { type: 'value', values: [v] as (string | number | boolean)[] } as const;
};

export const groupParameterDiscoveryJobs = (jobs: ParameterDiscoveryJob[], analyses: AvailableAnalysis[]): GroupedJobs => {
    const combinationsMap = new Map<string, JobCombo>();

    // Add all jobs
    jobs.forEach((job) => {
        const key = `${job.duration}-${job.horizonSeconds}`;
        const jobStatus = job.status;

        const status: JobCombo['status'] =
            jobStatus === 'running' || jobStatus === 'pending'
                ? jobStatus
                : jobStatus === 'failed'
                  ? 'failed'
                  : jobStatus === 'completed'
                    ? 'completed'
                    : 'none';

        combinationsMap.set(key, {
            duration: job.duration,
            horizonSeconds: job.horizonSeconds,
            status,
            job
        });
    });

    // Add completed analyses
    analyses.forEach((analysis) => {
        const key = `${analysis.duration}-${analysis.horizonSeconds}`;
        const existing = combinationsMap.get(key);

        if (existing) {
            existing.analysis = analysis;

            if (existing.status === 'none' || existing.status === 'completed') {
                existing.status = 'completed';
            }
        } else {
            combinationsMap.set(key, {
                duration: analysis.duration,
                horizonSeconds: analysis.horizonSeconds,
                status: 'completed',
                analysis
            });
        }
    });

    const completed: JobCombo[] = [];
    const active: JobCombo[] = [];
    const failed: JobCombo[] = [];

    combinationsMap.forEach((combo) => {
        const hasAnalysis = !!combo.analysis;

        if (combo.status === 'completed' && hasAnalysis) {
            completed.push(combo);
        } else if (combo.status === 'completed' && !hasAnalysis && combo.job) {
            // Job claims completed but no cached analysis exists – treat as failed so users
            // are not misled and can re-run the analysis explicitly.
            failed.push({ ...combo, status: 'failed' });
        } else if (combo.status === 'running' || combo.status === 'pending') {
            active.push(combo);
        } else if (combo.status === 'failed') {
            failed.push(combo);
        }
    });

    const getCompletedAt = (combo: JobCombo): number => {
        const raw = combo.job?.completedAt ?? combo.analysis?.createdAt ?? null;
        return raw ? dayjs(raw).valueOf() : 0;
    };

    completed.sort((a, b) => getCompletedAt(b) - getCompletedAt(a));
    active.sort((a, b) => a.duration.localeCompare(b.duration) || a.horizonSeconds - b.horizonSeconds);
    failed.sort((a, b) => a.duration.localeCompare(b.duration) || a.horizonSeconds - b.horizonSeconds);

    return { completed, active, failed, all: Array.from(combinationsMap.values()) };
};
